import { useState } from "react";
import {
  validatePhoneNumber,
  validateDrivingLicenseNumber,
  validateRoutingNumber as validateRoutingNumberAPI,
} from "../../api/index";
import useSalesFormStore from "../stores/sales-form";

export const useEMSValidation = () => {
  const [validatingPhone, setValidatingPhone] = useState({});
  const [validatingDrivingLicense, setValidatingDrivingLicense] = useState({});
  const [validatingRoutingNumber, setValidatingRoutingNumber] = useState(false);
  const validationStatuses = useSalesFormStore((s) => s.validationStatuses);
  const setValidationStatus = useSalesFormStore((s) => s.setValidationStatus);
  const getValidatedPhoneNumber = useSalesFormStore(
    (s) => s.getValidatedPhoneNumber,
  );
  const addValidatedPhoneNumber = useSalesFormStore(
    (s) => s.addValidatedPhoneNumber,
  );
  const getValidatedDriverLicense = useSalesFormStore(
    (s) => s.getValidatedDriverLicense,
  );
  const addValidatedDriverLicense = useSalesFormStore(
    (s) => s.addValidatedDriverLicense,
  );
  const getValidatedRoutingNumber = useSalesFormStore(
    (s) => s.getValidatedRoutingNumber,
  );
  const addValidatedRoutingNumber = useSalesFormStore(
    (s) => s.addValidatedRoutingNumber,
  );

  const validatePhone = async (
    phoneNumber,
    fieldName,
    setFieldError,
    setFieldTouched,
  ) => {
    if (!phoneNumber || phoneNumber.replace(/\D/g, "").length !== 10) {
      setValidationStatus(fieldName, undefined);
      return true;
    }

    const cachedStatus = getValidatedPhoneNumber(phoneNumber);
    if (cachedStatus !== undefined) {
      setFieldTouched(fieldName, true);
      setValidationStatus(fieldName, cachedStatus);
      setFieldError(fieldName, cachedStatus ? null : "* Enter a valid phone");
      setValidatingPhone((prev) => ({ ...prev, [fieldName]: false }));
      return cachedStatus;
    }

    setValidatingPhone((prev) => ({ ...prev, [fieldName]: true }));

    try {
      const response = await validatePhoneNumber({ phone: phoneNumber });
      const isValid = !!response?.status;
      setFieldTouched(fieldName, true);
      setFieldError(fieldName, isValid ? null : "* Enter a valid phone");
      setValidationStatus(fieldName, isValid);
      addValidatedPhoneNumber(phoneNumber, isValid);
      return isValid;
    } catch (error) {
      setFieldError(fieldName, "Error validating phone number");
      setFieldTouched(fieldName, true);
      setValidationStatus(fieldName, false);
      return false;
    } finally {
      setValidatingPhone((prev) => ({ ...prev, [fieldName]: false }));
    }
  };

  const validateDrivingLicense = async (
    drivingLicense,
    drivingLicenseState,
    docType,
    fieldName,
    setFieldError,
    setFieldTouched,
  ) => {
    if (docType && docType !== "LOCAL_STATE_ID") {
      setValidationStatus(fieldName, undefined);
      return true;
    }

    if (!drivingLicense || !drivingLicenseState || drivingLicense.length < 4) {
      setValidationStatus(fieldName, undefined);
      return true;
    }

    const cachedStatus = getValidatedDriverLicense(
      drivingLicense,
      drivingLicenseState,
    );
    if (cachedStatus !== undefined) {
      setFieldTouched(fieldName, true);
      setValidationStatus(fieldName, cachedStatus);
      setFieldError(
        fieldName,
        cachedStatus ? null : "* Enter a valid ID number",
      );
      setValidatingDrivingLicense((prev) => ({ ...prev, [fieldName]: false }));
      return cachedStatus;
    }

    setValidatingDrivingLicense((prev) => ({ ...prev, [fieldName]: true }));

    try {
      const response = await validateDrivingLicenseNumber({
        drivingLicense: drivingLicense,
        drivingLicenseState: drivingLicenseState,
      });

      const isValid = !!response?.status;
      setFieldTouched(fieldName, true);
      setFieldError(fieldName, isValid ? null : "* Enter a valid ID number");
      setValidationStatus(fieldName, isValid);
      addValidatedDriverLicense(drivingLicense, drivingLicenseState, isValid);
      return isValid;
    } catch (error) {
      console.error("Driving license validation error:", error);
      setFieldError(fieldName, "Error validating ID number");
      setFieldTouched(fieldName, true);
      setValidationStatus(fieldName, false);
      return false;
    } finally {
      setValidatingDrivingLicense((prev) => ({ ...prev, [fieldName]: false }));
    }
  };

  const validateMultiplePhones = async (
    phoneFields,
    setFieldError,
    setFieldTouched,
  ) => {
    const validationPromises = [];

    for (const [fieldName, phoneNumber] of Object.entries(phoneFields)) {
      if (phoneNumber && phoneNumber.replace(/\D/g, "").length === 10) {
        validationPromises.push(
          validatePhone(phoneNumber, fieldName, setFieldError, setFieldTouched),
        );
      }
    }

    const results = await Promise.all(validationPromises);
    return results.every((result) => result === true);
  };

  const validateFormPhones = async (
    values,
    phoneFieldNames,
    setFieldError,
    setFieldTouched,
  ) => {
    const phoneFields = {};

    phoneFieldNames.forEach((fieldName) => {
      if (values[fieldName]) {
        phoneFields[fieldName] = values[fieldName];
      }
    });

    return await validateMultiplePhones(
      phoneFields,
      setFieldError,
      setFieldTouched,
    );
  };

  const validateRoutingNumber = async (routingNumber, setFieldError) => {
    if (!routingNumber || routingNumber.replace(/\D/g, "").length !== 9) {
      setValidationStatus("bank_routing", undefined);
      return { status: true };
    }

    const cachedResult = getValidatedRoutingNumber(routingNumber);
    if (cachedResult !== undefined) {
      setFieldError(
        "bank_routing",
        cachedResult.status ? null : "* Enter a valid routing number",
      );
      setValidationStatus("bank_routing", cachedResult.status);
      setValidatingRoutingNumber(false);
      return { status: cachedResult.status, bankName: cachedResult.bankName };
    }

    setValidatingRoutingNumber(true);

    try {
      const response = await validateRoutingNumberAPI({
        routingNumber: routingNumber,
      });
      const isValid = !!response?.status;
      const bankName = response?.bankName;

      setFieldError(
        "bank_routing",
        isValid ? null : "* Enter a valid routing number",
      );
      setValidationStatus("bank_routing", isValid);
      addValidatedRoutingNumber(routingNumber, isValid, bankName);

      return { status: isValid, bankName };
    } catch (error) {
      console.error("Routing number validation error:", error);
      setFieldError("bank_routing", "Error validating routing number");
      setValidationStatus("bank_routing", false);
      return { status: false };
    } finally {
      setValidatingRoutingNumber(false);
    }
  };

  return {
    validatePhone,
    validateMultiplePhones,
    validateFormPhones,
    validatingPhone,
    validateDrivingLicense,
    validatingDrivingLicense,
    validateRoutingNumber,
    validatingRoutingNumber,
    validationStatuses,
    setValidationStatus,
  };
};
